import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StaticSRPPage } from './StaticSRPPage';
import type { StaticSRPData } from '../../../types/srp-static';
import type { SrpViewModel } from '../../../features/srp/SrpViewModel';
import { fetchSrpViewModelClient } from '../../../utils/srp/fetch-srp-view-model-client';

export interface SRPShellProps {
  /** Data for the static view (from pageContext.data) */
  data: StaticSRPData;
  locale: string;
  pathname: string;
  /** The full interactive tree (SavedState + SearchProvider + SearchWrapper + children). Rendered when user goes interactive. */
  interactiveTree: React.ReactNode;
  /**
   * When true (slim serialized payload: no inlined Algolia blob), prefetch `/api/srp-state` before mounting
   * the interactive tree and pass the result up so SearchProvider can paint with the same results as the shell.
   */
  isSlimPayload?: boolean;
  /** Identity for the current listing URL; when it changes, in-flight view-model cache is cleared. */
  navigationKey: string;
  /** Called when the client view model is available (prefetch completes). May run before or as we go interactive. */
  onClientViewModelReady?: (vm: SrpViewModel | null) => void;
}

type VmCache = { key: string; promise: Promise<SrpViewModel | null> };

const PREFETCH_TIMEOUT_MS = 12_000;

/**
 * Option B: Renders static SRP first (no providers). When the user goes interactive
 * (requestIdleCallback or first interaction), mounts the full tree with all providers.
 *
 * Slim payloads: prefetch full view model first so static shell can show real cards (via parent merge) and
 * interactive SearchProvider seeds with the same `initialResults`, reducing CLS when swapping shells.
 */
export function SRPShell({
  data,
  locale,
  pathname,
  interactiveTree,
  isSlimPayload = false,
  navigationKey,
  onClientViewModelReady,
}: SRPShellProps): React.ReactElement {
  const [isInteractive, setIsInteractive] = useState(false);
  const vmCacheRef = useRef<VmCache | null>(null);

  useEffect(() => {
    vmCacheRef.current = null;
  }, [navigationKey]);

  const getVmPromise = useCallback((): Promise<SrpViewModel | null> => {
    if (typeof window === 'undefined') {
      return Promise.resolve(null);
    }
    const href = window.location.href;
    const cached = vmCacheRef.current;
    if (cached?.key === navigationKey) {
      return cached.promise;
    }
    const promise = Promise.race([
      fetchSrpViewModelClient(href),
      new Promise<SrpViewModel | null>((resolve) => {
        setTimeout(() => resolve(null), PREFETCH_TIMEOUT_MS);
      }),
    ]);
    vmCacheRef.current = { key: navigationKey, promise };
    return promise;
  }, [navigationKey]);

  /** Prefetch as soon as slim SRP mounts so static shell can upgrade from skeleton to real cards. */
  useEffect(() => {
    if (!isSlimPayload || typeof window === 'undefined') return;
    let cancelled = false;
    getVmPromise().then((vm) => {
      if (!cancelled) onClientViewModelReady?.(vm);
    });
    return () => {
      cancelled = true;
    };
  }, [isSlimPayload, navigationKey, getVmPromise, onClientViewModelReady]);

  const goInteractive = useCallback(() => {
    if (!isSlimPayload) {
      setIsInteractive(true);
      return;
    }
    getVmPromise().then((vm) => {
      onClientViewModelReady?.(vm);
      setIsInteractive(true);
    });
  }, [isSlimPayload, getVmPromise, onClientViewModelReady]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onIdle = window.requestIdleCallback
      ? window.requestIdleCallback(goInteractive, { timeout: 4000 })
      : setTimeout(goInteractive, 2000);

    const onInteraction = (): void => {
      goInteractive();
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      window.removeEventListener('focus', onInteraction, true);
      if (typeof onIdle === 'number') clearTimeout(onIdle);
      else if (typeof onIdle === 'object' && typeof (onIdle as { cancel?: () => void }).cancel === 'function')
        (onIdle as { cancel: () => void }).cancel();
    };

    window.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
    window.addEventListener('keydown', onInteraction, { once: true, passive: true });
    window.addEventListener('focus', onInteraction, { once: true, capture: true });

    return () => {
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      window.removeEventListener('focus', onInteraction, true);
      if (typeof onIdle === 'number') clearTimeout(onIdle);
      else if (typeof onIdle === 'object' && typeof (onIdle as { cancel?: () => void }).cancel === 'function')
        (onIdle as { cancel: () => void }).cancel();
    };
  }, [goInteractive]);

  if (isInteractive) {
    return <>{interactiveTree}</>;
  }

  return <StaticSRPPage data={data} locale={locale} pathname={pathname} />;
}
